import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { CLUBS } from "../content.js";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EnquiryForm({ defaultClub = "", dark = false, testId = "enquiry-form", extraOptions = [], hideClub = false, source = "Website" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", club: defaultClub, message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const inputCls = `w-full rounded-lg border px-4 py-3.5 text-sm outline-none transition-colors duration-200 placeholder:text-faint focus:border-brand ${
    dark
      ? "border-white/20 bg-white/5 text-white placeholder:text-white/40 focus:bg-white/10"
      : "border-hairline bg-white text-navy focus:bg-mist"
  }`;

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/enquiries`, { ...form, source }, { timeout: 20000 });
      if (data.emailed) toast.success("Enquiry sent — our team will get in touch shortly.");
      else toast.success("Enquiry received — our team will get in touch shortly.");
      setSent(true);
      setForm({ name: "", email: "", phone: "", club: defaultClub, message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form data-testid={testId} onSubmit={submit} className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <input data-testid={`${testId}-name`} required value={form.name} onChange={set("name")} placeholder="Name" className={inputCls} />
        <input data-testid={`${testId}-phone`} required type="tel" value={form.phone} onChange={set("phone")} placeholder="Phone Number" className={inputCls} />
      </div>
      <input data-testid={`${testId}-email`} required type="email" value={form.email} onChange={set("email")} placeholder="Email" className={inputCls} />
      {hideClub ? (
        <input data-testid={`${testId}-club-hidden`} type="hidden" value={form.club} />
      ) : (
        <select data-testid={`${testId}-club`} required value={form.club} onChange={set("club")} className={`${inputCls} ${form.club ? "" : "text-faint"}`}>
          <option value="">Which Oneiros Club are you planning to join? *</option>
          {CLUBS.map((c) => (
            <option key={c.id} value={c.name} className="text-navy">
              {c.name}
            </option>
          ))}
          {extraOptions.map((o) => (
            <option key={o.value} value={o.value} className="text-navy">
              {o.label}
            </option>
          ))}
        </select>
      )}
      <textarea data-testid={`${testId}-message`} rows={4} value={form.message} onChange={set("message")} placeholder="Message" className={`${inputCls} resize-none`} />
      <button
        data-testid={`${testId}-submit`}
        type="submit"
        disabled={sending}
        className="group inline-flex items-center justify-center gap-3 rounded-full bg-brand px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-brand-dark disabled:opacity-60"
      >
        {sending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Sending
          </>
        ) : sent ? (
          <>
            <CheckCircle2 className="h-4 w-4" /> Message Sent
          </>
        ) : (
          <>
            Send Message <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </>
        )}
      </button>
    </form>
  );
}
